import { useMemo, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { api, formatApiErrorDetail } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { FastViLogo } from "../components/FastViLogo";
import { AlertTriangle, CheckCircle2, Eye, EyeOff, LogIn } from "lucide-react";

const MIN_LEN = 8;

export default function ResetPassword() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const token = (params.get("token") || "").trim();

  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const [apiError, setApiError] = useState("");

  const pwTooShort = password.length > 0 && password.length < MIN_LEN;
  const pwMismatch = confirm.length > 0 && confirm !== password;
  const canSubmit = useMemo(
    () => token && password.length >= MIN_LEN && confirm === password && !loading,
    [token, password, confirm, loading]
  );

  async function onSubmit(e) {
    e.preventDefault();
    if (!canSubmit) return;
    setLoading(true);
    setApiError("");
    try {
      await api.post("/auth/reset-password", { token, new_password: password });
      setDone(true);
    } catch (err) {
      setApiError(
        formatApiErrorDetail(err.response?.data?.detail) ||
          "Something went wrong. Please try again.",
      );
    } finally {
      setLoading(false);
    }
  }

  // --- State A: no token in URL -------------------------------------------
  if (!token) {
    return (
      <Shell>
        <div className="size-12 rounded-full bg-destructive/15 text-destructive grid place-items-center mb-4">
          <AlertTriangle size={20}/>
        </div>
        <div className="font-display text-3xl mb-2">Invalid reset link</div>
        <p className="text-sm text-muted-foreground">
          This link is missing its token. It may have been copied partially, or expired after use.
        </p>
        <div className="mt-6 flex items-center gap-2">
          <Link to="/forgot-password" className="flex-1">
            <Button className="w-full" data-testid="reset-request-new-link">Request a new link</Button>
          </Link>
          <Link to="/login" className="flex-1">
            <Button variant="outline" className="w-full" data-testid="reset-to-login">
              <LogIn size={14} className="mr-1.5"/> Sign in
            </Button>
          </Link>
        </div>
      </Shell>
    );
  }

  // --- State D: success ---------------------------------------------------
  if (done) {
    return (
      <Shell>
        <div className="size-12 rounded-full bg-emerald-500/15 text-emerald-600 grid place-items-center mb-4">
          <CheckCircle2 size={20}/>
        </div>
        <div className="font-display text-3xl mb-2" data-testid="reset-success-title">
          Password updated
        </div>
        <p className="text-sm text-muted-foreground">
          Your FastVi password has been changed. Log in with your new password to continue.
        </p>
        <Button
          onClick={() => navigate("/login", { replace: true })}
          className="w-full mt-6"
          data-testid="reset-success-login"
        >
          <LogIn size={14} className="mr-1.5"/> Sign in
        </Button>
      </Shell>
    );
  }

  // --- State B/C: form ----------------------------------------------------
  const expiredOrUsed = /expir|used|invalid/i.test(apiError);

  return (
    <Shell>
      <form onSubmit={onSubmit} data-testid="reset-form">
        <div className="font-display text-3xl mb-1">Choose a new password</div>
        <div className="text-sm text-muted-foreground mb-6">
          Pick something at least {MIN_LEN} characters. Everyone currently signed in to this account will be logged out.
        </div>

        <div className="space-y-4">
          <PasswordField
            id="rp-new"
            label="New password"
            value={password}
            onChange={setPassword}
            show={showPw}
            onToggle={() => setShowPw((v) => !v)}
            testId="reset-password"
            toggleTestId="reset-password-toggle"
            autoFocus
            invalid={pwTooShort}
          />
          {pwTooShort && (
            <div className="text-xs text-destructive -mt-2" data-testid="reset-pw-short">
              At least {MIN_LEN} characters, please.
            </div>
          )}

          <PasswordField
            id="rp-confirm"
            label="Confirm new password"
            value={confirm}
            onChange={setConfirm}
            show={showConfirm}
            onToggle={() => setShowConfirm((v) => !v)}
            testId="reset-confirm"
            toggleTestId="reset-confirm-toggle"
            invalid={pwMismatch}
          />
          {pwMismatch && (
            <div className="text-xs text-destructive -mt-2" data-testid="reset-pw-mismatch">
              Passwords don't match.
            </div>
          )}
        </div>

        {apiError && (
          <div className="mt-4 text-sm text-destructive" data-testid="reset-error">
            {apiError}
            {expiredOrUsed && (
              <div className="mt-1 text-xs text-muted-foreground">
                <Link to="/forgot-password" className="underline text-foreground"
                      data-testid="reset-error-request-new">
                  Request a new link
                </Link>
                {" "}and try again.
              </div>
            )}
          </div>
        )}

        <Button type="submit" className="w-full mt-6" disabled={!canSubmit}
                data-testid="reset-submit">
          {loading ? "Updating…" : "Update password"}
        </Button>

        <div className="mt-6 text-center text-sm">
          <Link to="/login" className="text-muted-foreground hover:text-foreground"
                data-testid="reset-cancel">
            Cancel
          </Link>
        </div>
      </form>
    </Shell>
  );
}

function PasswordField({ id, label, value, onChange, show, onToggle, testId, toggleTestId, autoFocus, invalid }) {
  return (
    <div>
      <Label className="label-tiny" htmlFor={id}>{label}</Label>
      <div className="relative mt-2">
        <Input
          id={id}
          type={show ? "text" : "password"}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          required
          autoFocus={autoFocus}
          minLength={MIN_LEN}
          aria-invalid={invalid || undefined}
          className={`pr-10 font-mono ${invalid ? "border-destructive focus-visible:ring-destructive" : ""}`}
          data-testid={testId}
        />
        <button
          type="button"
          onClick={onToggle}
          aria-label={show ? "Hide password" : "Show password"}
          className="absolute right-2 top-1/2 -translate-y-1/2 p-1.5 rounded-md text-muted-foreground hover:text-foreground"
          data-testid={toggleTestId}
        >
          {show ? <EyeOff size={14}/> : <Eye size={14}/>}
        </button>
      </div>
    </div>
  );
}

function Shell({ children }) {
  return (
    <div className="min-h-screen flex items-center justify-center bg-background grain p-6">
      <div className="w-full max-w-md card-stroke p-8 fade-up" data-testid="reset-password-card">
        <Link to="/" className="flex items-center gap-2 mb-6" data-testid="reset-brand-home">
          <FastViLogo size={36}/>
          <span className="font-display text-2xl">Fast<span className="text-accent">Vi</span></span>
        </Link>
        {children}
      </div>
    </div>
  );
}
